/**
 * URL state for the resource list (GDN-2732).
 *
 * List state lives in the query string so a filtered, sorted, paged list is
 * something you can hand to someone else. The subtlety is that the table
 * component announces its filters on mount and again whenever the change
 * handler's identity changes — not only when a human edits one. A handler that
 * treats every announcement as "the filters changed, go back to page 1" resets
 * the page on every render, which is what broke paging.
 */

/** Query params the list shell owns; everything else is a type's own filter. */
export const RESERVED_LIST_PARAMS = new Set([
  'page',
  'per_page',
  'sort_by',
  'sort_order',
]);

export type ListFilters = Record<string, string>;

/** The filter params currently in the URL (everything the shell doesn't own). */
export function readFilters(params: URLSearchParams): ListFilters {
  const filters: ListFilters = {};
  params.forEach((value, key) => {
    if (!RESERVED_LIST_PARAMS.has(key)) filters[key] = value;
  });
  return filters;
}

/** Same keys, same values — order-independent. */
export function filtersEqual(a: ListFilters, b: ListFilters): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => a[key] === b[key]);
}

/**
 * Apply a filter announcement to the current URL params.
 *
 * Returns `null` when the filters are unchanged, which is the common case: the
 * table re-announces the same set constantly, and acting on it would drop the
 * page. Only a genuine change resets to page 1 — the rest of the shell's own
 * params (per_page, sort) are preserved either way.
 */
export function applyFilters(
  current: URLSearchParams,
  nextFilters: ListFilters,
): URLSearchParams | null {
  if (filtersEqual(readFilters(current), nextFilters)) return null;

  const next = new URLSearchParams();
  current.forEach((value, key) => {
    if (RESERVED_LIST_PARAMS.has(key)) next.set(key, value);
  });
  for (const [key, value] of Object.entries(nextFilters)) {
    if (value) next.set(key, value);
  }
  // A different filter set means a different result set — page 1.
  next.delete('page');
  return next;
}
